import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../contexts/NotificationContext';
import { hasNotificationApi, shouldUseDesktopNotifications } from '../utils/runtimeEnvironment';

interface NotificationPermissionRequestProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

const NotificationPermissionRequest: React.FC<NotificationPermissionRequestProps> = ({
  onPermissionGranted,
  onPermissionDenied
}) => {
  const { t } = useTranslation();
  const { requestPermission, isPermissionGranted } = useNotifications();
  const [showRequest, setShowRequest] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    if (!shouldUseDesktopNotifications()) return;
    // عرض طلب الإذن إذا لم يتم منحه بعد
    if (hasNotificationApi() && Notification.permission === 'default') {
      // تأخير عرض الطلب لتحسين تجربة المستخدم
      const timer = setTimeout(() => {
        setShowRequest(true);
      }, 3000); // 3 ثوانٍ بعد تحميل الصفحة

      return () => clearTimeout(timer);
    }
  }, []);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      // مهلة أمان: إذا لم يُجب المتصفح خلال 15 ثانية، نخرج من حالة التحميل
      const granted = await Promise.race([
        requestPermission(),
        new Promise<boolean>((resolve) => setTimeout(() => {
          console.warn('انتهت مهلة طلب إذن الإشعارات');
          resolve(false);
        }, 15000))
      ]);
      if (granted) {
        setShowRequest(false);
        onPermissionGranted?.();
      } else {
        onPermissionDenied?.();
      }
    } catch (error) {
      console.error('خطأ في طلب إذن الإشعارات:', error);
      onPermissionDenied?.();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowRequest(false);
    // إعادة عرض الطلب بعد 24 ساعة
    localStorage.setItem('notification_dismissed', Date.now().toString());
  };

  // عدم عرض الطلب إذا تم منح الإذن أو رفضه نهائياً (لا تلمس Notification إن لم يكن معرّفاً — WebView)
  if (!hasNotificationApi() || !shouldUseDesktopNotifications()) {
    return null;
  }
  if (isPermissionGranted || Notification.permission === 'denied') {
    return null;
  }

  // عدم عرض الطلب إذا تم رفضه مؤخراً
  const dismissedTime = localStorage.getItem('notification_dismissed');
  if (dismissedTime) {
    const timeDiff = Date.now() - parseInt(dismissedTime);
    const hoursAgo = timeDiff / (1000 * 60 * 60);
    if (hoursAgo < 24) {
      return null;
    }
  }

  if (!showRequest) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start space-x-3 rtl:space-x-reverse">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
            </svg>
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            {t('notifications.titles.prompt.title')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('notifications.titles.prompt.message')}
          </p>
          
          <div className="mt-3 flex space-x-2 rtl:space-x-reverse">
            <button
              type="button"
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('notifications.titles.prompt.enable')}
            >
              {isRequesting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('notifications.titles.prompt.enabling')}
                </>
              ) : (
                t('notifications.titles.prompt.enable')
              )}
            </button>
            
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label={t('notifications.titles.prompt.later')}
            >
              {t('notifications.titles.prompt.later')}
            </button>
          </div>
        </div>
        
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          aria-label={t('notifications.titles.prompt.close')}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationPermissionRequest;